import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { api, errorMessage } from '../lib/api';
import { timeAgo, plural } from '../lib/format';
import { useDocumentTitle, useLoad } from '../lib/hooks';
import type { Project } from '../lib/types';
import { useAuth } from '../state/auth';
import { AppShell } from '../components/AppShell';
import { Icon } from '../components/Icon';
import { Dialog, Empty, ErrorState, Field, PageLoading } from '../components/ui';

function NewProjectDialog({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [clientName, setClientName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { project } = await api.post<{ project: Project }>('/api/projects', { name, clientName, description });
      navigate(`/projects/${project.id}`);
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };

  return (
    <Dialog title="New project" description="One project per client engagement. Add websites, creative and video inside it." onClose={onClose}>
      <form className="stack" style={{ gap: 16 }} onSubmit={submit}>
        <Field label="Project name">{(id) => <input id={id} className="input" placeholder="Acme website relaunch" value={name} onChange={(e) => setName(e.target.value)} required />}</Field>
        <Field label="Client">{(id) => <input id={id} className="input" placeholder="Acme Inc." value={clientName} onChange={(e) => setClientName(e.target.value)} />}</Field>
        <Field label="Notes" hint="Optional. Shown to everyone on the project.">
          {(id) => <textarea id={id} className="textarea" value={description} onChange={(e) => setDescription(e.target.value)} />}
        </Field>
        {error && (
          <div className="notice notice-error" role="alert">
            {error}
          </div>
        )}
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" disabled={busy}>
            {busy && <span className="spinner" />}
            Create project
          </button>
        </div>
      </form>
    </Dialog>
  );
}

export function DashboardPage() {
  useDocumentTitle('Projects');
  const { isStaff, scopeProjectId } = useAuth();
  const [creating, setCreating] = useState(false);
  const { data, error, loading, reload } = useLoad(() => api.get<{ projects: Project[] }>('/api/projects'), []);

  if (scopeProjectId) return <Navigate to={`/projects/${scopeProjectId}`} replace />;
  if (loading && !data) return <PageLoading label="Loading projects" />;
  if (error) {
    return (
      <AppShell>
        <ErrorState message={error} onRetry={() => reload()} />
      </AppShell>
    );
  }

  const projects = data?.projects ?? [];
  // A client with a single project has nothing to choose between.
  if (!isStaff && projects.length === 1) return <Navigate to={`/projects/${projects[0].id}`} replace />;
  const active = projects.filter((p) => p.status === 'active');
  const archived = projects.filter((p) => p.status === 'archived');

  return (
    <AppShell>
      <div className="page-head">
        <div>
          <h1>Projects</h1>
          <p>{isStaff ? 'Every client engagement, with its websites, creative and video.' : 'The projects you have been invited to review.'}</p>
        </div>
        {isStaff && (
          <button className="btn btn-primary" onClick={() => setCreating(true)}>
            <Icon name="plus" /> New project
          </button>
        )}
      </div>

      {projects.length === 0 ? (
        <Empty
          icon="folder"
          title={isStaff ? 'Create your first project' : 'No projects yet'}
          action={
            isStaff ? (
              <button className="btn btn-primary" onClick={() => setCreating(true)}>
                <Icon name="plus" /> New project
              </button>
            ) : undefined
          }
        >
          {isStaff
            ? 'A project holds everything one client needs to review: staging sites, static creative and video cuts.'
            : 'When MK-Way shares work with you it will show up here.'}
        </Empty>
      ) : (
        <>
          <div className="grid-cards">
            {active.map((project) => (
              <ProjectCard key={project.id} project={project} />
            ))}
          </div>
          {archived.length > 0 && (
            <div className="section">
              <h2>Archived</h2>
              <div className="grid-cards">
                {archived.map((project) => (
                  <ProjectCard key={project.id} project={project} />
                ))}
              </div>
            </div>
          )}
        </>
      )}
      {creating && <NewProjectDialog onClose={() => setCreating(false)} />}
    </AppShell>
  );
}

function ProjectCard({ project }: { project: Project }) {
  return (
    <Link to={`/projects/${project.id}`} className="project-card">
      <div className="stack" style={{ gap: 4 }}>
        <div className="row-between">
          <h3 className="truncate">{project.name}</h3>
          {project.gate?.enabled && (
            <span className="badge" title="Project password is on">
              <Icon name="lock" size={12} /> Shared
            </span>
          )}
        </div>
        <span className="muted truncate">{project.clientName || 'No client set'}</span>
      </div>
      <div className="stat-row">
        <div className="stat">
          <span className="stat-value">{project.counts.openComments}</span>
          <span className="stat-label">open</span>
        </div>
        <div className="stat">
          <span className="stat-value">{project.counts.resolvedComments}</span>
          <span className="stat-label">resolved</span>
        </div>
        <div className="stat">
          <span className="stat-value">{project.counts.items}</span>
          <span className="stat-label">{project.counts.items === 1 ? 'item' : 'items'}</span>
        </div>
      </div>
      <span className="muted" style={{ fontSize: 'var(--fs-xs)' }}>
        Updated {timeAgo(project.updatedAt)} · {plural(project.counts.files, 'file')}
      </span>
    </Link>
  );
}
